import type { Match, Router } from 'found';
import { useSelector } from 'react-redux';

import { SearchFilterApplied } from '../../ampli';
import { selectUser } from '../../reducers/user.selectors';
import services from '../../services';
import { getAmpliSearchEventPayload, getCurrentSearchId } from '../../utils/ampli';
import isCoursePage from '../../utils/isCoursePage';
import { serializeQuery } from '../../utils/serializeQuery';

const useQueryModification = <T extends Record<string, any>>(router: Router, match: Match) => {
	const user = useSelector(selectUser);
	/**
	 * Update query params with given params
	 * Remove query param when value is null, undefined, 0, '0', or empty array
	 * @param params
	 */
	const updateQueryParams = (params: T) => {
		const newQuery: { [key: string]: string } = {};
		const currentQuery = { ...(match.location.query as Record<string, any>) };

		Object.entries(params).forEach(([param, value]) => {
			if (!value || value.length === 0) {
				delete currentQuery[param];
				return;
			}

			if (Array.isArray(value)) {
				value = value.join(',');
			}

			newQuery[param] = value.toString();
		});

		const query = { ...currentQuery, ...newQuery } as Record<string, any>;
		if (serializeQuery(match.location.query as Record<string, any>) === serializeQuery(query)) {
			return;
		}

		if (user) {
			const searchPayload = getAmpliSearchEventPayload({
				query,
				previousQuery: match.location.query as Record<string, any>,
				term: query.term,
				appSection: isCoursePage(match.location.pathname) ? 'courses' : 'library',
				searchId: getCurrentSearchId(),
			});

			if (searchPayload) {
				const event = new SearchFilterApplied(searchPayload as any);
				services.track.ampliEventTrack({ event });
			}
		}

		router.push({
			pathname: match.location.pathname,
			query,
		});
	};

	const clearQueryParams = (params: (keyof T)[]) => {
		const query: { [key: string]: null } = {};
		// biome-ignore lint/suspicious/useIterableCallbackReturn: pre-existing callback shape; changing returns needs per-site behavior review
		// biome-ignore lint/suspicious/noAssignInExpressions: pre-existing assignment-in-expression; extracting it risks changing evaluation order
		params.forEach((param) => (query[param as string] = null));

		updateQueryParams(query as T);
	};

	const clearQueryParam = (param: keyof T) => clearQueryParams([param]);

	return {
		clearQueryParams,
		clearQueryParam,
		updateQueryParams,
	};
};

export default useQueryModification;
